import {
    ChangeDetectionStrategy,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

/** The kit's own default dialog layer: `z-index: var(--ph-layer-dialog, 1150)`. */
const KIT_DIALOG_LAYER_DEFAULT = 1150;

/** How far above the highest open dialog this one sits; matches the kit's own +10 step. */
const LAYER_STEP = 10;

/**
 * SD-10162: the one answer an org gets when it tries a Treasury action that second underwriting has
 * not released yet — moving money out, pulling money in, or issuing a spend card. It replaced the
 * "request early verification" dialog: there is nothing to request any more, the review runs on its
 * own.
 *
 * Callers open it only once the action itself would otherwise succeed (the balance covers it) and
 * only while the review is genuinely under way (`awaitsSecondUnderwriting`), so the approval is the
 * only thing standing in the way. The server refuses the same actions regardless.
 *
 * A kit dialog: the caller owns `open` and drops this into its own template. The message is body
 * copy, the kit's shape for a notice (a `description` is a subtitle over a body, and an empty body
 * still draws its band); size is the kit default, `xs`, which the kit gives confirms and notices.
 */
@Component({
    selector: 'app-approval-required-dialog',
    templateUrl: './approval-required-dialog.component.html',
    // The kit sets body copy; only the paragraph's own margin needs clearing.
    styles: ['.message { margin: 0; }'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ApprovalRequiredDialogComponent implements OnChanges {
    @Input() open = false;
    @Output() closed = new EventEmitter<void>();

    @ViewChild('dialog', { static: true }) private _dialog: ElementRef<HTMLElement>;

    readonly message = 'All new associations must be reviewed before accessing some features on PayHOA. '
        + 'Your association is still under review, which will be complete in 1-2 business days.';

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.open && this.open) {
            this.raiseAboveOpenDialogs();
        }
    }

    /**
     * This dialog usually opens from inside another kit dialog (Create Payable, Pay Invoice), and it
     * must land on top of it. Equal layers would do that by DOM order, but the kit lifts a dialog
     * opened while a Material overlay is on screen to just above `.cdk-overlay-container` (1200 here),
     * and leaves one opened later at its 1150 default. Create Payable opened from Move Money is
     * exactly that case: Move Money's Material pane is still animating out when Create Payable opens,
     * so it gets 1210, and this dialog, opened a step later with no pane left, drew underneath it.
     *
     * So take the layer the open dialogs actually have, and sit one step above the highest. Runs before
     * the `open` binding reaches the element, so the kit reads the raised layer when it opens; with no
     * other dialog open, the kit's own layering is left alone.
     */
    private raiseAboveOpenDialogs(): void {
        const own = this._dialog?.nativeElement;
        if (!own) {
            return;
        }

        const others = Array.from(document.querySelectorAll<HTMLElement>('[data-ph-drawer-layer] > ph-dialog'))
            .filter(dialog => dialog !== own && (dialog as HTMLElement & { open?: boolean }).open);

        if (others.length === 0) {
            own.style.removeProperty('--ph-layer-dialog');
            return;
        }

        const highest = Math.max(...others.map(dialog => this.layerOf(dialog)));
        own.style.setProperty('--ph-layer-dialog', String(highest + LAYER_STEP));
    }

    /**
     * The layer an open kit dialog is drawn at: the variable on its host where the kit set it there,
     * or else its backdrop's computed z-index inside the shadow root, which is what actually stacks.
     */
    private layerOf(dialog: HTMLElement): number {
        const onHost = parseInt(getComputedStyle(dialog).getPropertyValue('--ph-layer-dialog'), 10);
        const backdrop = dialog.shadowRoot?.querySelector<HTMLElement>('[class*="ph-layer-dialog"]');
        const drawn = backdrop ? parseInt(getComputedStyle(backdrop).zIndex, 10) : NaN;

        return Math.max(onHost || 0, drawn || 0) || KIT_DIALOG_LAYER_DEFAULT;
    }
}
